import * as React from 'react'
import {useEffect} from 'react'
import {useForceUpdate} from '@containers/hooks'

interface TransitionProps<T> {
  input: T
  time: number
  className?: string
  children: (arg: T) => React.ReactNode
}

/**
 * Class useful for implementing css-transitions.
 *
 * Example. Suppose we have component
 * <MyComp myProp={some}/>
 * and it is not rendered if myProp is not set.
 * But we want to transition this component based on myProp value.
 *
 * <Transition
 *   in={myProp}
 *   time={300}
 *   className={'my-comp' + (myProp ? ' active' : '')}
 * >
 *  {savedProp => <MyComp myProp={savedProp}/>}
 * </Transition>
 */
export const Transition = <T,>({
  children,
  input,
  time,
  ...otherProps
}: TransitionProps<T>) => {
  const savedInput = React.useRef(input)
  const forceUpdate = useForceUpdate()

  const toDisplay = input ?? savedInput.current
  useEffect(() => {
    if (!input) {
      if (time) {
        const timeoutHandler = setTimeout(() => {
          savedInput.current = input
          forceUpdate()
        }, time)

        return () => clearTimeout(timeoutHandler)
      }
    } else {
      savedInput.current = input
    }
    return undefined
  }, [input])

  return <div {...otherProps}>{children(toDisplay)}</div>
}
